import React from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import Typography from '@mui/material/Typography'
import ModalContainer from '../ModalContainer'
import { hideModal, showSuccessNotification, showErrorNotification } from '../../../redux/actions'
import { useQuerySessionsQuery, useRevokeAllSessionsMutation } from '../../../redux/slices/sessionsSlice'

export function RevokeAllSessionsModal({ user }) {
  const dispatch = useDispatch()
  const {
    data: sessions = [],
    isLoading: isLoadingSessions,
    isError: isSessionsError,
  } = useQuerySessionsQuery(user.id, { skip: !user.id })
  const [revoke, { isLoading: isRevoking }] = useRevokeAllSessionsMutation()

  const hasSessions = sessions.length > 0
  const canRevoke = hasSessions && !isSessionsError && !isLoadingSessions

  const onCancel = () => dispatch(hideModal())
  const onConfirm = async () => {
    try {
      const result = await revoke({ subjectId: user.id }).unwrap()
      dispatch(showSuccessNotification(`Signed the user out of ${result.revokedCount} session(s).`))
    } catch (error) {
      dispatch(showErrorNotification('Failed to revoke the user\'s sessions. Please try again.'))
    } finally {
      // Always dismiss — never leave the dialog stranded open after the call completes.
      dispatch(hideModal())
    }
  }

  let body
  if (isLoadingSessions) {
    body = <Typography style={{ marginTop: '10px' }}>Checking for active sessions…</Typography>
  } else if (isSessionsError) {
    body = (
      <Typography style={{ marginTop: '10px' }}>
        We couldn’t load this user’s active sessions right now. Please close this dialog and try again later.
      </Typography>
    )
  } else if (!hasSessions) {
    body = (
      <Typography style={{ marginTop: '10px' }}>
        This user has no active sessions to revoke.
      </Typography>
    )
  } else {
    body = (
      <>
        <Typography style={{ marginTop: '10px' }}>
          Are you sure you want to revoke all active sessions for this user?
        </Typography>
        <Typography style={{ marginTop: '10px' }}>
          They will be immediately signed out of every device and connected app, and will
          need to sign in again.
        </Typography>
      </>
    )
  }

  return (
    <ModalContainer
      title="Revoke All Sessions"
      modal
      maxWidth="xs"
      fullWidth="true"
      processing={isRevoking}
      actions={{
        primaryAction: { label: 'Revoke', onClick: onConfirm, disabled: !canRevoke },
        secondaryAction: { label: 'Cancel', onClick: onCancel },
      }}
    >
      {body}
    </ModalContainer>
  )
}

RevokeAllSessionsModal.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
}

export default RevokeAllSessionsModal
